import type { BBox, DxfPart, Placement } from './dxfNesting';
import { transformInsertEntity } from './dxfNesting';
import {
  buildMinimalDxf,
  type DxfCircleEnt,
  type DxfLineSeg,
  type DxfPointEnt,
} from './dxfMinimalAscii';

/* Nested DXF: minimal ASCII (LINE/CIRCLE/POINT). Bulge arcs match the nesting **SVG preview**
 * (same tx/ty + arcFromBulge), sampled via SVG <path> — not raw OCS bulge math. */

/** Same radius / large-arc / sweep as `entityToSvgPaths` → `arcFromBulge(..., true)`. */
function arcSvgFragmentFromBulge(
  x1: number, y1: number, x2: number, y2: number, bulge: number,
): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const chord = Math.sqrt(dx * dx + dy * dy);
  if (chord < 1e-8) return ` L ${x2} ${y2}`;
  const sagitta = Math.abs(bulge) * chord / 2;
  const radius = ((chord / 2) ** 2 + sagitta ** 2) / (2 * sagitta);
  const largeArc = Math.abs(bulge) > 1 ? 1 : 0;
  const sweep = (bulge > 0) === true ? 1 : 0;
  return ` A ${radius} ${radius} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
}

/**
 * Points along the fillet in **part OCS** (same numbers as parsed DXF), matching the
 * on-screen SVG preview. Uses browser SVG path sampling when available.
 */
function sampleBulgeEdgeLikeSvgPreview(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  bulge: number | undefined,
  bbox: BBox,
  segments: number,
): { x: number; y: number }[] {
  if (bulge == null || Math.abs(bulge) < 1e-10) return [p0, p1];
  const offsetX = bbox.minX;
  const offsetY = bbox.minY;
  const scaleY = bbox.height;
  const tx = (x: number) => x - offsetX;
  const ty = (y: number) => scaleY - (y - offsetY);
  const x1 = tx(p0.x);
  const y1 = ty(p0.y);
  const x2 = tx(p1.x);
  const y2 = ty(p1.y);
  const dx = x2 - x1;
  const dy = y2 - y1;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-10) return [p0, p1];

  const d = `M ${x1} ${y1}${arcSvgFragmentFromBulge(x1, y1, x2, y2, bulge)}`;

  if (typeof document !== 'undefined') {
    try {
      const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pathEl.setAttribute('d', d);
      const len = pathEl.getTotalLength();
      if (len < 1e-8) return [p0, p1];
      const out: { x: number; y: number }[] = [];
      for (let i = 0; i <= segments; i++) {
        const pt = pathEl.getPointAtLength((i / segments) * len);
        const ox = pt.x + bbox.minX;
        const oy = bbox.minY + bbox.height - pt.y;
        out.push({ x: ox, y: oy });
      }
      return out;
    } catch {
      /* fall through */
    }
  }
  return bulgeArcPointChainOcsFallback(p0, p1, bulge, segments);
}

/** DXF OCS bulge math with minor-arc angle — used when `document` is unavailable. */
function bulgeArcPointChainOcsFallback(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  bulge: number,
  segments: number,
): { x: number; y: number }[] {
  const theta0 = 4 * Math.atan(bulge);
  const theta = Math.abs(theta0) <= Math.PI + 1e-9
    ? theta0
    : (theta0 >= 0 ? 1 : -1) * (2 * Math.PI - Math.abs(theta0));
  const bEff = Math.tan(theta / 4);
  const chord = Math.hypot(p1.x - p0.x, p1.y - p0.y);
  const c = chord / 2;
  const sinHalf = Math.sin(theta / 2);
  if (Math.abs(sinHalf) < 1e-14) return [p0, p1];
  const r = Math.abs(c / sinHalf);
  const mx = (p0.x + p1.x) / 2;
  const my = (p0.y + p1.y) / 2;
  const ux = (p1.x - p0.x) / chord;
  const uy = (p1.y - p0.y) / chord;
  const nx = -uy * Math.sign(bEff);
  const ny = ux * Math.sign(bEff);
  const hOff = r * Math.cos(theta / 2);
  const cx = mx + nx * hOff;
  const cy = my + ny * hOff;
  const a0 = Math.atan2(p0.y - cy, p0.x - cx);
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const ang = a0 + theta * t;
    out.push({ x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) });
  }
  return out;
}

function shortestArcSweep(sa: number, ea: number): number {
  let d = ea - sa;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

/** Nesting: top-left origin Y↓; DXF sheet: bottom-left Y↑. */
export function partPointToSheetDxf(
  ox: number,
  oy: number,
  bbox: BBox,
  pl: Placement,
  sheetHeight: number,
): { x: number; y: number } {
  const nx = ox - bbox.minX;
  const ny = bbox.maxY - oy;
  let sx: number;
  let sy: number;
  if (!pl.rotated) {
    sx = nx;
    sy = ny;
  } else {
    sx = ny;
    sy = bbox.width - nx;
  }
  const sheetX = pl.x + sx;
  const sheetYFromTop = pl.y + sy;
  const yDxf = sheetHeight - sheetYFromTop;
  return { x: sheetX, y: yDxf };
}

interface Accum {
  lines: DxfLineSeg[];
  circles: DxfCircleEnt[];
  points: DxfPointEnt[];
}

function pushLine(accum: Accum, x1: number, y1: number, x2: number, y2: number): void {
  accum.lines.push({ x1, y1, x2, y2 });
}

function transformEntityToExport(
  accum: Accum,
  entity: any,
  bbox: BBox,
  blocks: Record<string, any> | undefined,
  tp: (ox: number, oy: number) => { x: number; y: number },
  arcSeg: number,
): void {
  const blocksSafe = blocks || {};

  switch (entity.type) {
    case 'LINE':
      if (entity.vertices?.length >= 2) {
        const a = entity.vertices[0];
        const b = entity.vertices[1];
        const p1 = tp(a.x, a.y);
        const p2 = tp(b.x, b.y);
        pushLine(accum, p1.x, p1.y, p2.x, p2.y);
      }
      break;

    case 'LWPOLYLINE':
    case 'POLYLINE': {
      const v = entity.vertices;
      if (!v?.length) break;
      const closed = !!entity.shape;
      const nEdge = closed ? v.length : v.length - 1;
      for (let i = 0; i < nEdge; i++) {
        const i1 = closed ? (i + 1) % v.length : i + 1;
        const p0 = v[i];
        const p1 = v[i1];
        const chain = sampleBulgeEdgeLikeSvgPreview(p0, p1, p0.bulge, bbox, arcSeg);
        for (let k = 0; k < chain.length - 1; k++) {
          const a = tp(chain[k].x, chain[k].y);
          const b = tp(chain[k + 1].x, chain[k + 1].y);
          pushLine(accum, a.x, a.y, b.x, b.y);
        }
      }
      break;
    }

    case 'CIRCLE':
      if (entity.center) {
        const c = tp(entity.center.x, entity.center.y);
        accum.circles.push({ cx: c.x, cy: c.y, r: entity.radius });
      }
      break;

    case 'ARC':
      if (entity.center) {
        const sa = entity.startAngle ?? 0;
        const ea = entity.endAngle != null ? entity.endAngle : sa + (entity.angleLength ?? 0);
        const sweep = shortestArcSweep(sa, ea);
        const eaUse = sa + sweep;
        const cx = entity.center.x;
        const cy = entity.center.y;
        const r = entity.radius;
        const steps = 24;
        let prev = tp(cx + r * Math.cos(sa), cy + r * Math.sin(sa));
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const ang = sa + t * (eaUse - sa);
          const cur = tp(cx + r * Math.cos(ang), cy + r * Math.sin(ang));
          pushLine(accum, prev.x, prev.y, cur.x, cur.y);
          prev = cur;
        }
      }
      break;

    case 'ELLIPSE':
      if (entity.center && entity.majorAxisEndPoint) {
        const mx = entity.majorAxisEndPoint.x;
        const my = entity.majorAxisEndPoint.y;
        const a = Math.sqrt(mx * mx + my * my);
        const b = a * (entity.axisRatio || 1);
        const rot = Math.atan2(my, mx);
        const cx = entity.center.x;
        const cy = entity.center.y;
        const steps = 36;
        let prev = tp(cx + a * Math.cos(rot), cy + a * Math.sin(rot));
        for (let i = 1; i <= steps; i++) {
          const t = (i / steps) * 2 * Math.PI;
          const lx = a * Math.cos(t);
          const ly = b * Math.sin(t);
          const wx = cx + lx * Math.cos(rot) - ly * Math.sin(rot);
          const wy = cy + lx * Math.sin(rot) + ly * Math.cos(rot);
          const cur = tp(wx, wy);
          pushLine(accum, prev.x, prev.y, cur.x, cur.y);
          prev = cur;
        }
      }
      break;

    case 'SPLINE':
      if (entity.controlPoints?.length >= 2) {
        const cp = entity.controlPoints;
        let prev = tp(cp[0].x, cp[0].y);
        for (let i = 1; i < cp.length; i++) {
          const cur = tp(cp[i].x, cp[i].y);
          pushLine(accum, prev.x, prev.y, cur.x, cur.y);
          prev = cur;
        }
      }
      break;

    case 'SOLID':
    case '3DFACE': {
      const pts = entity.points || entity.vertices;
      if (pts?.length >= 3) {
        for (let i = 0; i < pts.length; i++) {
          const j = (i + 1) % pts.length;
          if (!pts[i] || !pts[j]) continue;
          const a = tp(pts[i].x, pts[i].y);
          const b = tp(pts[j].x, pts[j].y);
          pushLine(accum, a.x, a.y, b.x, b.y);
        }
      }
      break;
    }

    case 'POINT':
      if (entity.position) {
        const p = tp(entity.position.x, entity.position.y);
        accum.points.push({ x: p.x, y: p.y });
      }
      break;

    case 'INSERT':
      if (blocksSafe[entity.name]) {
        for (const be of blocksSafe[entity.name].entities || []) {
          const te = transformInsertEntity(be, entity);
          transformEntityToExport(accum, te, bbox, blocksSafe, tp, arcSeg);
        }
      }
      break;
    default:
      break;
  }
}

export interface ExportNestedDxfOptions {
  sheetWidth: number;
  sheetHeight: number;
  placements: Placement[];
  partById: Map<string, DxfPart>;
  /** 4 = mm, 1 = inches (DXF $INSUNITS) */
  insunits: number;
}

export function buildNestedDxfString(opts: ExportNestedDxfOptions): string {
  const { sheetWidth, sheetHeight, placements, partById, insunits } = opts;

  const accum: Accum = { lines: [], circles: [], points: [] };
  const arcSeg = 32;

  for (const pl of placements) {
    const part = partById.get(pl.partId);
    if (!part) continue;
    const bbox = part.bbox;
    const blocks = part.blocks;
    const tp = (ox: number, oy: number) => partPointToSheetDxf(ox, oy, bbox, pl, sheetHeight);

    for (const e of part.rawEntities) {
      transformEntityToExport(accum, e, bbox, blocks, tp, arcSeg);
    }
  }

  return buildMinimalDxf(accum.lines, accum.circles, accum.points, {
    insunits: insunits,
    extMin: { x: 0, y: 0 },
    extMax: { x: sheetWidth, y: sheetHeight },
  });
}
